import React from 'react';
import { Shield, Clock, Globe, CheckCircle2, Star, Users, Video, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { T } from '@/components/T';

const ConversionOptimizedFeatures: React.FC = () => {
  const features = [
    {
      icon: Shield,
      title: 'Verified Professionals',
      description: 'All Therapists Certified',
      highlight: 'Hundred Percent Verified',
      color: 'text-green-600'
    },
    {
      icon: Globe,
      title: 'Global Coverage',
      description: 'Connect Professionals Language',
      highlight: 'Countries Plus',
      color: 'text-blue-600'
    },
    {
      icon: Video,
      title: 'Secure Video Sessions',
      description: 'Hipaa Compliant Encrypted',
      highlight: 'Bank Grade Security',
      color: 'text-purple-600'
    },
    {
      icon: Clock,
      title: 'Flexible Scheduling',
      description: 'Book Sessions Schedule',
      highlight: 'Same Day Available',
      color: 'text-orange-600'
    },
    {
      icon: Users,
      title: 'Personalized Matching',
      description: 'Advanced Algorithm Matches',
      highlight: 'Success Rate',
      color: 'text-teal-600'
    },
    {
      icon: MessageCircle,
      title: 'Multiple Languages',
      description: 'Therapy Sessions Languages',
      highlight: 'Languages Plus',
      color: 'text-indigo-600'
    }
  ];

  const stats = [
    { label: 'Active Professionals', value: '15,000+', icon: Users },
    { label: 'Countries Covered', value: '150+', icon: Globe },
    { label: 'Sessions Completed', value: '2.5M+', icon: CheckCircle2 },
    { label: 'Average Rating', value: '4.9★', icon: Star }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            <T>Why Choose Platform</T>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            <T>Trusted By Millions</T>
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {stats.map((stat, index) => (
            <Card key={index} className="text-center p-4 bg-primary/5 border-primary/20">
              <CardContent className="p-0">
                <stat.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground"><T>{stat.label}</T></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="relative overflow-hidden hover:shadow-lg transition-all duration-300 group border-border/50 hover:border-primary/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full bg-muted/50 ${feature.color} group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-foreground"><T>{feature.title}</T></h3>
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-primary/10 text-primary border-primary/20">
                        <T>{feature.highlight}</T>
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      <T>{feature.description}</T>
                    </p>
                  </div>
                </div>
              </CardContent>
              
              {/* Subtle hover effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </Card>
          ))}
        </div>

        {/* Trust indicators */}
        <div className="mt-12 text-center">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-600" />
              <span><T>Hipaa Compliant</T></span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-blue-600" />
              <span><T>Iso Certified</T></span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-600" />
              <span><T>Rating Four Nine</T></span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-purple-600" />
              <span><T>Endorsed By Dr Marin</T></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConversionOptimizedFeatures;